import React, { createContext, useContext, useMemo, useState } from 'react'

const STORAGE_KEY = 'oms-template-preview-settings'

const defaultSettings = {
  previewZoom: 1,
  tableDensity: 'comfortable',
  fontScale: 1
}

const sanitizeSettings = (input) => {
  const parsedZoom = Number(input?.previewZoom)
  const parsedFontScale = Number(input?.fontScale)
  const zoom = Number.isFinite(parsedZoom) ? Math.min(1.3, Math.max(0.8, parsedZoom)) : defaultSettings.previewZoom
  const fontScale = Number.isFinite(parsedFontScale) ? Math.min(1.2, Math.max(0.9, parsedFontScale)) : defaultSettings.fontScale
  const density = ['compact', 'comfortable', 'spacious'].includes(input?.tableDensity)
    ? input.tableDensity
    : defaultSettings.tableDensity

  return {
    previewZoom: Number(zoom.toFixed(2)),
    tableDensity: density,
    fontScale: Number(fontScale.toFixed(2))
  }
}

const getInitialSettings = () => {
  if (typeof window === 'undefined') return defaultSettings

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaultSettings
    const parsed = JSON.parse(raw)
    return sanitizeSettings(parsed)
  } catch (_error) {
    return defaultSettings
  }
}

const TemplatePreviewContext = createContext(null)

export function TemplatePreviewProvider({ children }) {
  const [settings, setSettings] = useState(getInitialSettings)

  const persistAndSet = (nextSettings) => {
    setSettings((prev) => {
      const resolved = typeof nextSettings === 'function' ? nextSettings(prev) : nextSettings
      const sanitized = sanitizeSettings(resolved)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized))
      }
      return sanitized
    })
  }

  const value = useMemo(() => ({
    ...settings,
    setPreviewZoom: (previewZoom) => persistAndSet((prev) => ({ ...prev, previewZoom })),
    setTableDensity: (tableDensity) => persistAndSet((prev) => ({ ...prev, tableDensity })),
    setFontScale: (fontScale) => persistAndSet((prev) => ({ ...prev, fontScale })),
    resetTemplatePreviewSettings: () => persistAndSet(defaultSettings)
  }), [settings])

  return (
    <TemplatePreviewContext.Provider value={value}>
      {children}
    </TemplatePreviewContext.Provider>
  )
}

export function useTemplatePreviewSettings() {
  const context = useContext(TemplatePreviewContext)
  if (!context) {
    throw new Error('useTemplatePreviewSettings must be used within TemplatePreviewProvider')
  }
  return context
}
